import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Store, Pause, Play, Trash2, Tag, ArrowLeftRight, ChevronRight } from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatPrice } from '../utils/formatters';
import { getListingStatusConfig, LISTING_TYPE_LABELS } from '../constants/marketplace';

const MyMarketplaceListings = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadListings = async () => {
    try {
      const response = await api.get('/marketplace/my-listings');
      setListings(response.data.listings || []);
    } catch (err) {
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadListings();
  }, []);

  const handleTogglePause = async (listing) => {
    const newStatus = listing.status === 'active' ? 'paused' : 'active';
    try {
      await api.put(`/marketplace/listings/${listing.id}`, { status: newStatus });
      loadListings();
    } catch (err) {
      setError('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (listing) => {
    if (!window.confirm('Retirer cette annonce de la marketplace ?')) return;
    try {
      await api.delete(`/marketplace/listings/${listing.id}`);
      loadListings();
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };


  if (loading) return <div className="py-12"><LoadingSpinner size="lg" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
            <Store className="h-7 w-7 text-carmin" />
            Mes annonces Marketplace
          </h1>
          <p className="text-sm text-muted mt-1">{listings.length} annonce{listings.length !== 1 ? 's' : ''}</p>
        </div>
        <Link
          to="/marketplace"
          className="inline-flex items-center text-sm text-carmin hover:text-carmin-light"
        >
          Marketplace <ChevronRight className="h-4 w-4 ml-0.5" />
        </Link>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-md mb-6">{error}</div>
      )}

      {listings.length === 0 ? (
        <div className="text-center py-16">
          <Store className="h-16 w-16 text-muted mx-auto mb-4" />
          <h3 className="text-lg font-medium text-primary mb-2">Aucune annonce</h3>
          <p className="text-muted mb-4">
            Mettez vos véhicules en vente depuis leur page de détail.
          </p>
          <Link
            to="/dashboard"
            className="inline-flex items-center px-4 py-2 bg-carmin text-white rounded-md hover:bg-carmin-light"
          >
            Voir mes véhicules
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {listings.map(listing => {
            const vehicle = listing.vehicle || {};
            const status = getListingStatusConfig(listing.status);

            return (
              <div key={listing.id} className="bg-dark-card rounded-lg border border-dark-border p-4">
                <div className="flex items-start gap-4">
                  {/* Thumbnail */}
                  <div className="w-24 h-18 flex-shrink-0">
                    {vehicle.images?.[0] ? (
                      <img src={vehicle.images[0]} alt="" className="w-24 h-18 rounded object-cover" />
                    ) : (
                      <div className="w-24 h-18 rounded bg-dark-hover flex items-center justify-center text-muted text-xs">
                        Pas d'image
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-primary truncate">
                        {vehicle.make} {vehicle.model}
                      </h3>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 text-sm text-muted">
                      <span className="inline-flex items-center gap-1">
                        {listing.listing_type === 'exchange' ? <ArrowLeftRight className="h-3 w-3" /> : <Tag className="h-3 w-3" />}
                        {LISTING_TYPE_LABELS[listing.listing_type]}
                      </span>
                      {listing.pro_price && (
                        <span className="font-medium text-carmin">{formatPrice(listing.pro_price)}</span>
                      )}
                      <span>{vehicle.year} - {vehicle.mileage?.toLocaleString('fr-BE')} km</span>
                    </div>

                    {listing.exchange_details && (
                      <p className="text-xs text-accent-purple mt-1">Recherche : {listing.exchange_details}</p>
                    )}
                    {listing.notes && (
                      <p className="text-xs text-muted mt-1 truncate">Note : {listing.notes}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {(listing.status === 'active' || listing.status === 'paused') && (
                      <button
                        onClick={() => handleTogglePause(listing)}
                        className="p-2 text-muted hover:text-label hover:bg-dark-hover rounded"
                        title={listing.status === 'active' ? 'Mettre en pause' : 'Réactiver'}
                      >
                        {listing.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(listing)}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
                      title="Retirer de la marketplace"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyMarketplaceListings;
